import { Box, Button, Group, Image, Modal, Paper, Stack, Text, Textarea, TextInput } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconPhoto } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import appIcon from '@/assets/images/icon.png'
import { FileUpload } from '@/components/FileUpload'
import { MODAL_OVER_MODAL_Z_INDEX } from '@/configs/componentDefaults'
import { useGlobalState } from '@/hooks/useGlobalState'
import * as commonClasses from '@/styles/componentCommons.css'
import { toBareS3Key } from '@/utils/uploads'

import {
  BODY_MAX_LENGTH,
  IMAGE_MAX_BYTES,
  IMAGE_MIME_TYPES,
  isDuplicateSendError,
  sendErrorMessage,
  TITLE_MAX_LENGTH,
  titlePrefix,
} from './notifications'

const confirmDefaults = { centered: true, groupProps: { mt: 'xl' }, zIndex: MODAL_OVER_MODAL_Z_INDEX } as const

interface CreateNotificationModalProps {
  opened: boolean
  onClose: () => void
}

export function CreateNotificationModal({ opened, onClose }: CreateNotificationModalProps) {
  const { api, configStore } = useGlobalState()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [image, setImage] = useState<File | null>(null)
  const [isSending, setIsSending] = useState(false)
  const sendingRef = useRef(false)

  const placeholdersQuery = useQuery({
    queryKey: ['terminology', 'placeholder'],
    queryFn: () => api.getPlaceholderTerminology(),
    enabled: opened,
  })

  const sponsorInfoQuery = useQuery({
    queryKey: ['sponsor', 'info'],
    queryFn: () => api.getSponsorInfo(),
    enabled: opened,
  })

  const imagePreviewUrl = useMemo(() => (image ? URL.createObjectURL(image) : null), [image])
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl)
    }
  }, [imagePreviewUrl])

  const brandImage = sponsorInfoQuery.data?.brandImage
  const brandImageUrl = brandImage ? `${configStore.serverBaseUrl}/files/${brandImage}` : null

  // A failed background refetch keeps the cached names, so gate on data rather than isSuccess.
  const placeholdersLoaded = placeholdersQuery.data !== undefined
  const prefix = titlePrefix(placeholdersQuery.data)
  const titleMaxLength = Math.max(0, TITLE_MAX_LENGTH - prefix.length)
  const fullTitle = `${prefix}${title.trim()}`
  const canSend =
    !!title.trim() && !!body.trim() && fullTitle.length <= TITLE_MAX_LENGTH && placeholdersLoaded && !isSending

  const titleError =
    !placeholdersLoaded && placeholdersQuery.isError
      ? "Your channel name couldn't be loaded. Close this and try again."
      : placeholdersLoaded && titleMaxLength === 0
        ? 'Your channel name leaves no room for a title. Set a shorter Channel Short Name in Channel Branding.'
        : fullTitle.length > TITLE_MAX_LENGTH
          ? `Shorten the title to fit the ${TITLE_MAX_LENGTH}-character limit, including your channel name.`
          : undefined

  const handleClose = () => {
    if (sendingRef.current) return
    setTitle('')
    setBody('')
    setImage(null)
    onClose()
  }

  const send = async (allowDuplicate: boolean) => {
    if (sendingRef.current) return
    sendingRef.current = true
    setIsSending(true)
    try {
      // Uploaded on every attempt: the server may already have consumed an earlier upload, even on a 409.
      let imageKey: string | undefined
      if (image) {
        const prepared = await api.uploadPrepare({ fileType: image.type })
        await api.upload({ file_path: prepared.filePath, file_content: image })
        imageKey = toBareS3Key(prepared.filePath)
      }
      await api.sendPushNotification({
        title: fullTitle,
        body: body.trim(),
        ...(allowDuplicate && { allow_duplicate: true }),
        ...(imageKey && { image_key: imageKey }),
      })
      sendingRef.current = false
      notifications.show({
        title: 'Notification sent',
        message: 'Your notification is on its way to your End Users.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      void api.queryClient.invalidateQueries({ queryKey: ['notifications', 'history'] })
      handleClose()
    } catch (error) {
      if (isDuplicateSendError(error)) {
        modals.openConfirmModal({
          ...confirmDefaults,
          title: 'Resend message?',
          children: (
            <Text size="sm">
              You recently sent this exact message. Sending this again will result in users getting duplicates of the
              same message.
            </Text>
          ),
          labels: { confirm: 'Resend', cancel: 'Cancel' },
          onConfirm: () => void send(true),
        })
      } else {
        notifications.show({
          title: 'Notification not sent',
          message: sendErrorMessage(error),
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      }
    } finally {
      sendingRef.current = false
      setIsSending(false)
    }
  }

  const handleSendClick = () => {
    modals.openConfirmModal({
      ...confirmDefaults,
      title: 'Send notification?',
      children: (
        <Text size="sm">
          This sends your notification to every End User in your channel now. A sent notification can&apos;t be recalled
          or edited.
        </Text>
      ),
      labels: { confirm: 'Send', cancel: 'Cancel' },
      onConfirm: () => void send(false),
    })
  }

  return (
    <Modal opened={opened} onClose={handleClose} title="Create Notification" size="lg" centered>
      <Stack gap="md">
        <TextInput
          label="Title"
          required
          value={title}
          onChange={e => setTitle(e.currentTarget.value)}
          maxLength={titleMaxLength}
          description={
            prefix ? `Starts with "${prefix}", which counts toward the ${TITLE_MAX_LENGTH}-character limit.` : undefined
          }
          rightSection={
            <Text size="xs" c="dimmed">
              {prefix.length + title.length}/{TITLE_MAX_LENGTH}
            </Text>
          }
          rightSectionWidth={56}
          disabled={isSending || !placeholdersLoaded}
          error={titleError}
        />
        <Textarea
          label="Message"
          required
          value={body}
          onChange={e => setBody(e.currentTarget.value)}
          maxLength={BODY_MAX_LENGTH}
          rows={4}
          description={`${body.length}/${BODY_MAX_LENGTH}`}
          inputWrapperOrder={['label', 'input', 'description']}
          disabled={isSending}
        />

        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Image
          </Text>
          {image && imagePreviewUrl ? (
            <Paper withBorder p="sm" radius="md">
              <Group gap="md" wrap="nowrap">
                <Image src={imagePreviewUrl} alt="" w={48} h={48} radius="sm" fit="cover" />
                <Text size="sm" fw={500} truncate style={{ flex: 1, minWidth: 0 }}>
                  {image.name}
                </Text>
                <Button size="xs" variant="outline" color="red" onClick={() => setImage(null)} disabled={isSending}>
                  Remove
                </Button>
              </Group>
            </Paper>
          ) : (
            <FileUpload
              accept={IMAGE_MIME_TYPES}
              maxSize={IMAGE_MAX_BYTES}
              maxFiles={1}
              onDrop={files => setImage(files[0] ?? null)}
              disabled={isSending}
              icons={{ idle: IconPhoto }}
              titles={{ idle: 'Drag & drop an image here, or browse' }}
              hints={{ idle: 'Optional. PNG or JPG up to 20 MB. Without one, your Channel Icon is used.' }}
              primaryActionLabel="Browse"
              actionHidden
            />
          )}
        </Stack>

        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Preview
          </Text>
          <Paper withBorder radius="lg" p="sm" bg="gray.0">
            <Group gap="sm" align="flex-start" wrap="nowrap">
              <Image src={appIcon} alt="" w={32} h={32} radius="sm" />
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={600} style={{ wordBreak: 'break-word' }}>
                  {title.trim() ? fullTitle : `${prefix}Notification title`}
                </Text>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {body.trim() || 'Your message'}
                </Text>
              </Box>
              {(imagePreviewUrl ?? brandImageUrl) && (
                <Image src={imagePreviewUrl ?? brandImageUrl} alt="" w={48} h={48} radius="sm" fit="cover" />
              )}
            </Group>
          </Paper>
        </Stack>

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={handleClose} disabled={isSending}>
            Cancel
          </Button>
          <Button onClick={handleSendClick} disabled={!canSend} loading={isSending}>
            Send Notification
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
